import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Images, X } from "lucide-react";
import { motion } from "framer-motion";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { api, imgSrc } from "@/lib/api";
import { Reveal, SectionLabel } from "./Reveal";

const Cover = ({ items, onOpen }) => {
  const item = items[0];
  return (
    <motion.button
      type="button"
      data-testid="gallery-cover"
      onClick={() => onOpen(0)}
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className="group relative block w-full aspect-[4/5] sm:aspect-[16/10] lg:aspect-[16/8] overflow-hidden bg-black border border-white/10 text-left"
    >
      <img src={imgSrc(item.image_url)} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover scale-125 blur-2xl opacity-40" />
      <img src={imgSrc(item.image_url)} alt={item.caption || item.artists || "JayPanduh show photo"} className="absolute inset-0 w-full h-full object-contain group-hover:scale-[1.03] transition-transform duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
      <div className="absolute inset-x-0 bottom-0 p-5 md:p-8 flex flex-col md:flex-row md:items-end justify-between gap-4">
        <div>
          {item.artists && <p className="font-display uppercase text-4xl md:text-6xl leading-none">{item.artists}</p>}
          {item.event && <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#56ED4A] mt-2">{item.event}</p>}
        </div>
        <span data-testid="gallery-view-all" className="self-start md:self-auto inline-flex items-center gap-3 bg-[#56ED4A] text-black px-5 py-4 font-mono text-xs uppercase tracking-[0.2em] font-semibold group-hover:bg-white transition-colors">
          <Images size={16} /> View all {items.length} photo{items.length === 1 ? "" : "s"}
        </span>
      </div>
    </motion.button>
  );
};

const Lightbox = ({ items, index, setIndex }) => {
  const item = items[index];
  const go = useCallback((d) => setIndex((i) => (i + d + items.length) % items.length), [items.length, setIndex]);
  useEffect(() => {
    if (index === null) return undefined;
    const onKey = (e) => { if (e.key === "ArrowRight") go(1); if (e.key === "ArrowLeft") go(-1); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, go]);
  return (
    <Dialog open={index !== null} onOpenChange={(o) => !o && setIndex(null)}>
      <DialogContent data-testid="gallery-lightbox" className="max-w-5xl w-[95vw] max-h-[95dvh] overflow-y-auto grid-cols-[minmax(0,1fr)] bg-black border-white/10 p-3 sm:p-4 [&>button]:hidden">
        <DialogTitle className="sr-only">Show photo</DialogTitle>
        {item && (
          <div className="min-w-0 w-full">
            <div className="relative">
              <img key={item.id} src={imgSrc(item.image_url)} alt={item.caption || item.artists || "Show photo"} className="w-full h-[55vh] sm:h-[70vh] object-contain" />
              <button data-testid="gallery-prev-btn" onClick={() => go(-1)} aria-label="Previous" className="absolute left-2 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-black/70 border border-white/20 hover:bg-[#56ED4A] hover:text-black"><ChevronLeft /></button>
              <button data-testid="gallery-next-btn" onClick={() => go(1)} aria-label="Next" className="absolute right-2 top-1/2 -translate-y-1/2 w-11 h-11 grid place-items-center bg-black/70 border border-white/20 hover:bg-[#56ED4A] hover:text-black"><ChevronRight /></button>
              <button data-testid="gallery-close-btn" onClick={() => setIndex(null)} aria-label="Close" className="absolute right-2 top-2 w-10 h-10 grid place-items-center bg-black/70 border border-white/20"><X size={18} /></button>
            </div>
            <div className="pt-3 px-1 flex flex-wrap items-end justify-between gap-2">
              <div>
                {item.artists && <p className="font-display uppercase text-3xl leading-none">{item.artists}</p>}
                {item.event && <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#56ED4A] mt-1">{item.event}</p>}
                {item.caption && <p className="text-zinc-300 text-sm mt-2">{item.caption}</p>}
              </div>
              <p className="font-mono text-xs text-zinc-400">{index + 1} / {items.length}</p>
            </div>
            <div className="mt-3 flex gap-2 overflow-x-auto pb-1" data-testid="gallery-thumbs">
              {items.map((t, n) => (
                <button key={t.id} data-testid={`gallery-thumb-${n}`} onClick={() => setIndex(n)} className={`shrink-0 w-16 h-16 bg-[#121216] border-2 ${n === index ? "border-[#56ED4A]" : "border-transparent opacity-60 hover:opacity-100"}`}>
                  <img src={imgSrc(t.image_url)} alt="" loading="lazy" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export const Gallery = () => {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/gallery").then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);
  return (
    <section id="gallery" data-testid="gallery-section" className="px-5 md:px-10 py-24 md:py-36 bg-[#0d0d10]">
      <Reveal>
        <SectionLabel index="05">The archive</SectionLabel>
        <div className="mt-6 grid lg:grid-cols-2 gap-8 items-end">
          <h2 className="font-display uppercase text-6xl md:text-8xl lg:text-9xl leading-[0.85]">Nights <span className="chrome-text">we've</span> thrown</h2>
          <p className="text-zinc-300 text-base md:text-lg max-w-md lg:justify-self-end">Past shows, the artists we've shared the stage with and the people who pulled up.</p>
        </div>
      </Reveal>
      <div className="mt-14">
        {items.length > 0 && <Cover items={items} onOpen={setOpen} />}
        {!items.length && (
          <div data-testid="gallery-coming-soon" className="border border-dashed border-white/20 py-20 text-center">
            <img src="/logo-mark.png" alt="" className="w-24 h-24 mx-auto object-contain opacity-80" />
            <p className="font-display uppercase text-4xl mt-6">Photos dropping soon</p>
          </div>
        )}
      </div>
      <Lightbox items={items} index={open} setIndex={setOpen} />
    </section>
  );
};
